import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import { css } from '@emotion/react'

import Link from '@/components/common/Link'

import { mediaUp, mediaDown } from '@/styles/media'
import { colors, weights } from '@/styles/theme'
import { Text2Styles } from '@/styles/typography'
import { focusVisible, underlineHover } from '@/styles/mixins'

import { getArrowIcon } from '@/constants/arrowAssets'

import Submenu from './Submenu'

interface Props {
  click: boolean
  hover: boolean
  onClick?(): void
}

function FaqSubmenu(props: Props) {
  const { t } = useTranslation(['common', 'faq'])
  const { click = false, hover = false, onClick } = props
  const { pathname, query } = useRouter()
  const faqCategories = Array.isArray(
    t('faq:categories.list', { returnObjects: true })
  )
    ? t('faq:categories.list', { returnObjects: true })
    : []
  const mainLinkContent = (
    <Link href={{ pathname: '/faq' }} passHref prefetch={false}>
      <a
        onClick={onClick}
        css={[
          mainLinkItem,
          (pathname === '/faq' || pathname === '/faq/[category]') &&
            linkItemActive
        ]}
      >
        {t('common:navigation.submenuFaqsTitle')}
        <img src={getArrowIcon('arrow')} alt="Arrow icon" />
      </a>
    </Link>
  )

  const linksContent = (
    <ul css={content}>
      {faqCategories
        ?.slice(10)
        ?.sort((faq, nextFaq) => {
          return (faq?.category ?? '').localeCompare(nextFaq?.category ?? '')
        })
        .map((faq: any, index: number) => (
          <li css={faqLink} key={index}>
            <Link
              href={{
                pathname: '/faq/[category]',
                query: {
                  category: faq?.categoryUrlSlug
                }
              }}
              passHref
              prefetch={false}
            >
              <a
                onClick={onClick}
                css={[
                  faqLinkItem,
                  query.category === faq?.categoryUrlSlug && linkItemActive
                ]}
              >
                {faq?.category}
              </a>
            </Link>
          </li>
        ))}
    </ul>
  )

  return (
    <Submenu
      mainLinkContent={mainLinkContent}
      linksContent={linksContent}
      click={click}
      hover={hover}
    />
  )
}

const mainLinkItem = css`
  ${focusVisible};
  ${Text2Styles};
  ${underlineHover};
  font-weight: ${weights.black};
  color: ${colors.white};
  margin-top: 18px;
  display: flex;
  align-items: center;

  svg {
    margin-inline-start: 4px;

    ${mediaDown('lg')} {
      width: 9px;
      height: 9px;
    }

    html[lang^='ar'] & {
      transform: rotate(180deg);
    }
  }

  ${mediaDown('lg')} {
    font-size: 12px;
  }

  ${mediaUp('lg')} {
    margin-top: 0;
  }
`

const content = css`
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 5px;

  ${mediaUp('lg')} {
    padding: 20px;
    padding-top: 0;

    align-items: flex-start;
  }
`

const faqLink = css`
  display: flex;
  margin-top: 12px;

  ${mediaUp('lg')} {
    margin-top: 20px;
  }
`

const faqLinkItem = css`
  ${focusVisible};
  ${Text2Styles};
  ${underlineHover};
  font-weight: ${weights.black};
  color: ${colors.white};
  line-height: normal;
  white-space: nowrap;

  ${mediaDown('lg')} {
    font-size: 12px;
  }

  ${mediaUp('lg')} {
    color: ${colors.twilightBlue};
    line-height: normal;
  }
`

const linkItemActive = css`
  text-decoration: underline !important;
`

export default FaqSubmenu
